<template>
  <div class="recent-transactions-widget-container recent-transactions-widget-container--glass">
    <div v-if="loading" class="dashboard-widget-glass-state">
      <i class="bi bi-arrow-repeat dashboard-widget-glass-spinner" aria-hidden="true"></i>
      <span>Loading transactions…</span>
    </div>
    <div v-else-if="error" class="dashboard-widget-glass-state dashboard-widget-glass-state--error">
      <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
      <span>{{ error }}</span>
    </div>
    <div v-else class="dashboard-widget-glass-table-section consultant-glass-table-section">
      <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
        <div class="consultant-glass-table-scroll-x">
          <table class="consultant-glass-table recent-transactions-widget-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th class="dashboard-widget-glass-amount-header">Amount</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="transactions.length === 0">
                <tr>
                  <td colspan="4" class="consultant-glass-table-no-records">No recent transactions</td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="(transaction, index) in transactions"
                  :key="transaction.id"
                  class="consultant-glass-table-row"
                  :style="{ animationDelay: `${0.04 + index * 0.03}s` }"
                >
                  <td>{{ formatDate(transaction.date) }}</td>
                  <td>
                    <span class="dashboard-widget-glass-type-pill">{{ transaction.type }}</span>
                    <span
                      v-if="transaction.isBankRecorded"
                      class="rp-bank-linked-chip"
                      :title="bankRecordedTitle(transaction.bankRecord)"
                    >
                      <i class="bi bi-bank2" aria-hidden="true"></i>
                      Bank Recorded
                    </span>
                  </td>
                  <td class="recent-transactions-widget-description-cell">{{ transaction.description || '-' }}</td>
                  <td
                    class="dashboard-widget-glass-amount-cell"
                    :class="{
                      'dashboard-widget-glass-amount-cell--empty': !transaction.amount,
                      'dashboard-widget-glass-amount-cell--negative': transaction.amount < 0,
                    }"
                  >{{ formatCurrency(transaction.amount) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/RecentTransactionsWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import { formatBankRecordedTitle } from '@/utils/bankRecordBadge';

export default {
  name: 'RecentTransactionsWidget',
  props: {
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      transactions: [],
      loading: true,
      error: null,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    bankRecordedTitle(rec) {
      return formatBankRecordedTitle(rec, this.formatDate, this.formatCurrency);
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const [receivePayments, payBills, invoices, journalEntries] = await Promise.all([
          this.fetchReceivePayments().catch(() => []),
          this.fetchPayBills().catch(() => []),
          this.fetchInvoices().catch(() => []),
          this.fetchJournalEntries().catch(() => []),
        ]);

        const allTransactions = [
          ...receivePayments,
          ...payBills,
          ...invoices,
          ...journalEntries,
        ];

        this.transactions = allTransactions
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 10);
      } catch (error) {
        logger.error('Error fetching recent transactions:', error);
        this.error = 'Failed to load data';
        this.transactions = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchReceivePayments() {
      try {
        const response = await ApiService.get(`/customer/receive-payments?companyId=${this.companyId}`);
        const payments = Array.isArray(response.data) ? response.data : (response.data?.data || []);

        return payments.map((payment) => ({
          id: `payment-${payment.id}`,
          date: payment.paymentDate || payment.date,
          type: 'Payment Received',
          description: payment.reference || `Payment from ${payment.customer?.customerName || 'Customer'}`,
          amount: parseFloat(payment.amountReceived || payment.total || 0),
        }));
      } catch (error) {
        logger.error('Error fetching receive payments:', error);
        return [];
      }
    },
    async fetchPayBills() {
      try {
        const payBills = await ApiService.get(`/vendor/pay-bill/company/${this.companyId}`);
        const bills = Array.isArray(payBills) ? payBills : (payBills?.data || []);

        return bills.map((bill) => ({
          id: `paybill-${bill.id}`,
          date: bill.date,
          type: 'Bill Paid',
          description: bill.no || `Payment to ${bill.vendor?.vendorName || 'Vendor'}`,
          amount: -Math.abs(parseFloat(bill.total || 0)),
        }));
      } catch (error) {
        logger.error('Error fetching pay bills:', error);
        return [];
      }
    },
    async fetchInvoices() {
      try {
        const response = await ApiService.get(`/customer/invoices?companyId=${this.companyId}`);
        const invoices = Array.isArray(response.data) ? response.data : (response.data?.data || response.data?.rows || []);

        return invoices.map((invoice) => ({
          id: `invoice-${invoice.id}`,
          date: invoice.invoiceDate || invoice.date,
          type: 'Invoice',
          description: invoice.invoiceNo || `Invoice to ${invoice.customer?.customerName || 'Customer'}`,
          amount: parseFloat(invoice.totalAmount || invoice.total || 0),
          isBankRecorded: !!invoice.isBankRecorded,
          bankRecord: invoice.bankRecord || null,
        }));
      } catch (error) {
        logger.error('Error fetching invoices:', error);
        return [];
      }
    },
    async fetchJournalEntries() {
      try {
        const response = await ApiService.get(`/journal-entry/company/${this.companyId}?limit=10&order=DESC`);
        const entries = Array.isArray(response.data) ? response.data : (response.data?.entries || response.data?.data || []);

        return entries.map((entry) => ({
          id: `journal-${entry.id}`,
          date: entry.entryDate || entry.date,
          type: 'Journal Entry',
          description: entry.comments || entry.memo || `Journal Entry #${entry.entryNo || entry.id}`,
          amount: parseFloat(entry.totalDebit || entry.totalAmount || 0),
        }));
      } catch (error) {
        logger.error('Error fetching journal entries:', error);
        return [];
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      const absAmount = Math.abs(amount);
      const sign = amount < 0 ? '-' : '';
      return sign + new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(absAmount);
    },
  },
};
</script>
